<template>
  <div class="booking-summary">
    <FluidContainer>
      <div class="booking-summary__wrapper">
        <div>
          <div class="booking-summary__heading">
            <h1>{{ data.headline }}</h1>
            <hr>
            <i v-if="isFluidTablet">{{ data.subHeadline }}</i>
          </div>
          <div class="booking-summary__hotel">
            <p>{{ offer.HotelName }} <span :class="[ 'stars', 'stars--lightblue', hotel.HotelStandardStarsCss ]"/></p>
          </div>
          <div class="booking-summary__additional-service">
            <div>
              <div v-if="isFluidTablet" class="booking-summary__participants">
                <h2>{{ data.firstSectionText }}</h2>
                <hr>
              </div>
              <div class="booking-summary__pattern-element" v-for="participant in participants" :key="participant.id">
                <p class="booking-summary__pattern-text to-uppercase">
                  <span>{{ participant.id }}</span> {{ participant.title }} 
                  <span v-if="participant.type != 'adult'">(ur. {{ getChildrenBirthday(participant.birthday) }})</span>
                </p>
                <p class="price">{{ formattedParticipantPrice(participant) }} <span>{{ currency }}</span></p>
              </div>
            </div>
            <div class="booking-summary__additional-service--wrapper">
              <div v-if="insurances.length" class="booking-summary__services">
                <h2>Ubezpieczenia</h2>
                <hr>
                <div v-for="(service, key) in insurances" :key="key" class="booking-summary__pattern-element" :class="{ 'height-extension': isMobile && !service.IsOptional }">
                  <div class="booking-summary__pattern-text horizontal-align">
                    <p :title="service.Title">
                      <span>{{ service.Title }} / {{ selectedPeopleCount(service) }}</span>
                      <Icon v-if="service.TooltipHeader || service.TooltipBody" icon="info" class="icon__info"/>
                    </p>
                    <div class="tooltip-wrapper">
                      <div v-if="service.TooltipHeader || service.TooltipBody" class="booking-summary__services--toolTip">
                        <Icon icon="info" class="icon__info-toolTip" />
                        <h3 v-if="service.TooltipHeader">{{ service.TooltipHeader }}</h3>
                        <p v-if="service.TooltipBody">{{ service.TooltipBody }}</p>
                        <span @click="toggleInsurancesDetailsModal" class="details">Sprawdź szczegóły</span>
                      </div>
                    </div>
                  </div>
                  <p v-if="!service.IsOptional && service.ServicePriceAll == '0'" class="in-price">w cenie</p>
                  <p v-else class="price">{{ service.ServicePriceAll }} <span>{{ currency }}</span></p>
                </div>
              </div>

              <div v-if="attractions.length" class="booking-summary__attractions">
                <h2>Usługi dodatkowe</h2>
                <hr>
                <div v-for="(service, key) in attractions" :key="key" class="booking-summary__pattern-element">
                  <p class="booking-summary__pattern-text" :title="service.Title">{{ service.Title }} / {{ selectedPeopleCount(service) }}</p>
                  <p class="price">{{ service.ServicePriceAll }} <span>{{ currency }}</span></p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="booking-summary__column">
          <div v-if="dropdowns.voucher" class="booking-summary__voucher">
            <h3>KOD RABATOWY / VOUCHER</h3>
            <div class="booking-summary__voucher--input-field">
              <input @input="voucherCodeInput" type="text" id="voucherCode" placeholder="Tutaj wpisz kod rabatowy" :class="{ 'right-padding' : voucherCode}">
                <button v-if="voucherCode" @click="addVoucher" class="booking-summary__voucher--convert-button">
                  <p>PRZELICZ </p>
                  <Icon icon="v" class="icon_arrow"/>
                </button>
            </div>
          </div>
          <div>
            <div class="booking-summary__price">
              <div class="booking-summary__price--header" @click="toggle('modalInfo')">
                <h3>{{ data.totalPriceText }}</h3>
                <Icon icon="info" class="icon__info--red"/>
              </div>
              <p class="booking-summary__price--total">{{ formattedTotalPrice }} <span>{{ currency }}</span></p>
              <p class="booking-summary__price--advance">{{ data.instalmentText }} {{ formattedInstallmentPrice }} <span>{{ currency }}</span></p>
              <p class="booking-summary__price--remaining">{{ data.paymentAmountText }} {{ formattedRemainingPrice }} {{ currency }} {{ data.paymentDateText }} {{ remainingPaymentDate }}</p>
            </div>
            <div class="booking-summary__contract-template">
              <p v-html="data.paymentObligationText" />
              <button v-if="!isMobile" type="button" class="reservation-button" @click="bookReservation">{{ data.buttonText }}</button>
              <span v-if="isFluidTablet" v-html="data.contractTemplate" />
            </div>
          </div>
        </div>
      </div>
    <button v-if="isMobile" type="button" class="reservation-button" @click="bookReservation">{{ data.buttonText }}</button>
    </FluidContainer>

    <ModalRoot v-if="dropdowns.modalInfo" @close="toggle('modalInfo')" class="modal-dialog--small">
      <div class="booking-summary__modal">
        <div class="booking-summary__modal--header">
          <Icon icon="info" class="icon__info"/>
          <h1>CENA RAZEM ZAWIERA</h1>
        </div>
        <hr>
        <div v-for="item in priceContent" :key="item.id" class="booking-summary__modal--price-content">
          <Icon icon="form-valid-check" class="icon__form-valid-check" />
          <p>{{ item.title }}</p>
        </div>
      </div>
    </ModalRoot>

    <ModalRoot v-if="dropdowns.processingDataModal" :closeByClickingOutside="false" @close="toggle('processingDataModal')" class="modal-dialog--small">
      <div class="modal-dialog__content reservation-process">
        <Loader custom="spinner" class="reservation-process__loader" />
        <div class="reservation-process__headline">PRZYGOTOWUJEMY TWOJE ZAMÓWIENIE!</div>
        <hr>
        <div class="reservation-process__description">Za chwilę nastąpi przekierowanie do płatności.</div>
      </div>
    </ModalRoot>

    <OfferExpired v-if="dropdowns.processingDataError" @close="toggle('processingDataError')"
      :data="offerExpiredData" :fallbackUrl="offer.HotelUrl" />

    <DetailsModal v-if="dropdowns.insurancesDetailsModal" class="details-modal"
      @close="toggleInsurancesDetailsModal" title="Szczegóły ubezpieczenia" :footerButton="false" />
  </div>
</template>

<script>
import moment from 'moment'
import Vue from 'vue'
import Component from 'vue-class-component'
import { mapState, mapGetters } from 'vuex'

import FormStorageMixin from '@/mixins/FormStorageMixin'
import DetailsModal from '@/components/booking/additionalServices/Details'
import OfferExpiredLogic from '@/components/booking/logic/OfferExpired'
import OfferExpired from '@/components/booking/OfferExpired'
import { INSURANCES_CODE, ATTRACTIONS_CODE } from '@/components/booking/types'
import FluidContainer from '@/components/FluidContainer.vue'
import Icon from '@/components/Icon'
import Loader from '@/components/Loader'
import ModalRoot from '@/components/ModalRoot'
import DropdownHandler from '@/components/search/DropdownHandler'
import * as events from '@/events'
import { API } from '@/modules/api'
import BookingDataLayer from '@/modules/datalayer/Booking'
import Store from '@/store/index'
import MediaDetector from '@/utils/MediaDetector'
import { polishPlurals } from '@/utils/Plurals'
import { addSpace } from '@/utils/priceFormat'

@Component({
  store: Store,
  mixins: [
    MediaDetector,
    DropdownHandler,
    OfferExpiredLogic,
    FormStorageMixin,
  ],
  components: {
    FluidContainer,
    Icon,
    ModalRoot,
    Loader,
    OfferExpired,
    DetailsModal,
  },
  props: {
    data: {
      type: Object,
      required: false,
      default: () => {},
    },
  },
  computed: {
    ...mapGetters({
      totalPrice: 'Booking/getOfferTotalPrice',
      offer: 'Booking/getOffer',
      hotel: 'Booking/getHotel',
      user: 'Booking/getUser',
      paymentMethod: 'Booking/getPaymentMethod',
      selectedServices: 'Booking/getSelectedServices',
      rodoStatements: 'Booking/getRodoStatements',
    }),
    ...mapState({
      participants: state => state.Booking.participants,
    }),
    formattedRemainingPrice () {
      return addSpace(this.offer.RemainingAmount)
    },
    formattedTotalPrice () {
      return addSpace(this.totalPrice)
    },
    formattedInstallmentPrice () {
      return addSpace(this.offer.FirstInstallmentValue)
    },
    participants () {
      const storedData = this.loadFromStorage() || {}
      if (storedData.participants) {
        return storedData.participants.map(p => ({
          ...p,
          birthday: moment(p.birthday),
          type: p.type || 'adult',
          title: p.title || 'Osoba dorosła',
        }))
      }
      return []
    },
  },
  methods: {
    isTFG (service) {
      return service.MerlinCode == 'TFG_15'
    },
    formattedParticipantPrice (participant) {
      const offerPerson = this.offer.Persons.find(p => p.Id === participant.id)
      const price = offerPerson ? offerPerson.Price : 0
      
      if (!price || isNaN(price)) {
        return addSpace('0')
      }
      
      const result = addSpace(price.toString())
      return result
    },
    set participants (value) {
      const currentData = this.loadFromStorage() || {}
      this.saveToStorage({
        ...currentData,
        participants: value.map(p => ({
          ...p,
          birthday: p.birthday ? p.birthday.format('YYYY-MM-DD') : null,
        })),
      })
    },
  },
  mounted () {
    /* eslint-disable */
    window.offer_data = this.offer
  /* eslint-enable */
  },
})
export default class BookingSummary extends Vue {
  dropdowns = {
    tooltip: Array(2).fill().map(() => false),
    modalInfo: false,
    voucher: false,
    processingDataModal: false,
    processingDataError: false,
    insurancesDetailsModal: false,
  }
  pricePerPerson = '1 799'
  pricePerPersonChild = '830'
  currency = 'ZŁ'

  voucherCode = ''

  priceContent = [
    { id: 1, title: 'pobyt w hotelu' },
    { id: 2, title: 'wybrane wyżywienie' },
    { id: 3, title: 'przelot samolotem' },
    { id: 4, title: 'opłaty lotniskowe' },
    { id: 5, title: 'transfer do hotelu' },
    { id: 6, title: 'opiekę rezydenta' },
    { id: 7, title: 'ubezpieczenie podstawowe wszystkich uczestników wycieczki' },
    { id: 8, title: 'wybrane dodatkowe ubezpieczenie' },
    { id: 9, title: 'wybrane dodatkowe udogodnienia i atrakcje' },
    { id: 10, title: 'opłaty na turystyczny fundusz gwarancyjny' },
  ]

  get insurances () {
    const list = this.offer.SelectedServices
      .filter(service => service.ServiceTypeCode == INSURANCES_CODE && service.IsVisible)
    const isZero = list.filter(service => service.ServicePriceAll == 0)
    const isNotZero = list.filter(service => service.ServicePriceAll != 0).sort((a, b) => b.ServicePriceAll.compareTo(a.ServicePriceAll))

    return [ ...isZero, ...isNotZero ]
  }

  get attractions () {
    return this.offer.SelectedServices
      .filter(service => service.ServiceTypeCode == ATTRACTIONS_CODE && service.IsVisible)
      .sort((a, b) => b.ServicePriceAll.compareTo(a.ServicePriceAll))
  }

  get remainingPaymentDate () {
    return moment(this.offer.RemainingAmountEndDate).format('DD.MM.YYYY')
  }

  get customValidationFields () {
    return [ 'phoneNumber', 'zipCodeFirstPart', 'zipCodeSecondPart' ]
  }

  get children () {
    return this.participants.filter(person => person.type != 'adult')
  }

  get selectedAdditionalServices () {
    return this.selectedServices.map(service => ({
      Code: service.code,
      IsChecked: service.isChecked,
      SelectedPeople: service.selectedIDs,
    }))
  }

  /* eslint-disable */
  get reservationData () {
    return {
      OfferCode: this.offer.OfferCode,
      Adults: this.offer.AdultsCount,
      Children: this.offer.ChildrenCount,
      InformationFormName: this.user.firstName,
      InformationFormSurname: this.user.lastName,
      InformationFormEmail: this.user.email,
      InformationFormNumber: this.user.phone,
      InformationFormStreet: this.user.street,
      InformationFormHomeNumber: this.user.houseNumber,
      InformationFormFlatNumber: this.user.apartmentNumber,
      InformationFormCity: this.user.city,
      InformationFormPostalCode: this.user.zipCode,
      StatementBooking1: this.rodoStatements[0].value,
      StatementBooking2: this.rodoStatements[1].value,
      StatementBooking3: this.rodoStatements[2].value,
      StatementBooking4: this.rodoStatements[3].value,
      StatementBooking5: this.rodoStatements[4].value,
      TotalPrice: this.offer.TotalPrice,
      Payment: 'Przelewy24',
      People: this.participants.map(person => ({
        Id: person.id,
        Name: person.firstName,
        Surname: person.lastName,
        Gender: this.handleGenderCodeForMerlin(person.gender),
        Birthdate: moment(person.birthday).format('DD.MM.YYYY'),
        isChild: person.type != 'adult',
      })),
      AdditionalServices: this.selectedAdditionalServices,
      p24_method: this.paymentMethod,
    }
  }
  /* eslint-enable */

  formattedParticipantPrice (participant) {
    const offerPerson = this.offer.Persons.find(p => p.Id === participant.id)
    const price = offerPerson ? offerPerson.Price : 0
    
    if (!price || isNaN(price)) {
      return addSpace('0')
    }
    
    const result = addSpace(price.toString())
    return result
  }

  selectedPeopleCount (service) {
    const plurals = [ 'osoba', 'osoby', 'osób' ]
    const peopleCount = service.Adults + service.Children + service.Infants

    return `${peopleCount} ${polishPlurals(...plurals, peopleCount)}`
  }

  toggleInsurancesDetailsModal () {
    this.dropdowns.insurancesDetailsModal = !this.dropdowns.insurancesDetailsModal
  }

  handleGenderCodeForMerlin (gender) {
    if (gender == 'kobieta') return 'F'
    else if (gender == 'mężczyzna') return 'H'
    else return ''
  }

  getChildrenBirthday (date) {
    return moment(date).format('DD.MM.YYYY')
  }

  voucherCodeInput (e) {
    this.voucherCode = e.target.value
  }

  addVoucher () {
    log.debug('__Booking.Summary.addVoucher__')
  }

  splitFieldName (fieldName) {
    return fieldName.split('_')
  }

  fieldIsNotChildBirthday (field) {
    const [ fieldName, index ] = this.splitFieldName(field)
    
    if (fieldName === 'birthday') {
      const participant = this.participants[index - 1]
      return participant && participant.type === 'adult'
    }
    
    return true
  }

  validateUserData () {
    const fields = this.$validator._base.fields.items
      .map(field => field.name)
      .filter(this.fieldIsNotChildBirthday)

    const storedData = this.loadFromStorage() || {}
    const validationPromises = []

    fields.forEach(field => {
      const [ fieldName, index ] = this.splitFieldName(field)
      
      // For participants, validate all required fields
      if (index && storedData.participants && storedData.participants[index - 1]) {
        const participant = storedData.participants[index - 1]
        const requiredFields = [ 'firstName', 'lastName', 'gender', 'birthday' ]
        
        // Skip validation for first participant if additionalFields is true
        if (index === '1' && storedData.clientForm && storedData.clientForm.additionalFields) {
          return
        }
        
        // Modified this condition to properly handle birthday validation
        if (requiredFields.includes(fieldName)) {
          const value = this.getValueToValidate(field)
          // Special handling for birthday field
          if (fieldName === 'birthday') {
            if (!value || value === '' || (moment.isMoment(value) && value.isSame(moment(0)))) {
              validationPromises.push(
                this.$validator.validate(field, '')
              )
            }
          } else if (!participant[fieldName] || participant[fieldName] === '') {
            validationPromises.push(
              this.$validator.validate(field, value)
            )
          }
        }
        return
      }
      
      validationPromises.push(
        this.$validator.validate(field, this.getValueToValidate(field))
      )
    })

    // Add custom field validations
    this.customValidationFields.forEach(field => {
      validationPromises.push(this.$validator.validate(field))
    })

    // Add RODO validations
    const rodoFormAgreements = storedData.rodoFormAgreements || {}
    if (rodoFormAgreements.checklist) {
      const requiredStatements = rodoFormAgreements.checklist.filter(statement => statement.required)
      
      // First check if any required statements are not selected
      const hasUnselectedRequired = requiredStatements.some(statement => !statement.selected)
      if (hasUnselectedRequired) {
        this.$validator.errors.add({
          field: 'rodoStatements',
          msg: 'Proszę zaakceptować wymagane zgody',
        })
        return Promise.reject({ response: { status: '0' } })
      }
    }

    // Add adult age validation
    const hasAdultOver18 = this.validateAdultAgeRequirement(storedData.participants || [])
    if (!hasAdultOver18) {
      this.$validator.errors.add({
        field: 'adult_age_requirement',
        msg: 'Przynajmniej jedna osoba musi mieć ukończone 18 lat',
      })
      return Promise.reject({ response: { status: '0' } })
    }

    return Promise.all(validationPromises)
      .then(this.handleUserValidation)
  }

  getValueToValidate (fieldName) {
    const [ , index ] = this.splitFieldName(fieldName)

    if (this.user.hasOwnProperty(fieldName)) return this.user[fieldName]
    else if (index && this.rodoStatements[index - 1] && this.rodoStatements[index - 1].value) {
      return this.rodoStatements[index - 1].value
    }
    else if (index && this.participants[index - 1] && this.participants[index - 1].hasOwnProperty(fieldName)) {
      return this.participants[index - 1][fieldName]
    } 
    return ''
  }

  handleUserValidation () {
    if (this.$validator.errors.items.length) {
      new BookingDataLayer().formSubmissionFailed()
      return Promise.reject({ response: { status: '0' } })
    } else {
      new BookingDataLayer().formSubmissionSuccess()
      
      // Get stored participants data
      const storedData = this.loadFromStorage() || {}
      const participants = storedData.participants || []
      
      // Create the reservation data with People array
      const payload = {
        ...this.reservationData,
        People: participants.map(person => ({
          Name: person.firstName,
          Surname: person.lastName,
          Gender: this.handleGenderCodeForMerlin(person.gender),
          Birthdate: moment(person.birthday).format('DD.MM.YYYY'),
          isChild: person.type !== 'adult',
        })),
      }

      // Set the payload for the API call
      this.payload = payload
      
      return Promise.resolve(true)
    }
  }

  scrollPageToInvalidSection () {
    const el = document.querySelector('.field-invalid')
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }

  bookReservationErrorHandler ({ response }) {
    if (response.status == '0') setTimeout(this.scrollPageToInvalidSection, 0)
    else {
      this.toggle('processingDataModal')
      this.toggle('processingDataError')
      this.setOfferExpiredData(response)
    }
  }

  bookReservation () {
    this.validateUserData()
      .then(() => this.toggle('processingDataModal'))
      .then(() => new API().getBookingReservationData(this.payload))
      .then(url => { events.bookingOrderCreate(); return url })
      .then(url => window.location.replace(url.paymentUrl))
      .catch(this.bookReservationErrorHandler)
  }

  // Add method to validate adult age requirement in Summary
  validateAdultAgeRequirement (participants) {
    const adultsOver18 = participants.filter(participant => {
      if (!participant.birthday) {
        return false
      }
      
      const birthday = moment(participant.birthday)
      if (!birthday.isValid() || birthday.isSame(moment(0))) {
        return false
      }
      
      const age = moment().diff(birthday, 'years')
      const isAdult = age >= 18
      
      return isAdult
    })
    
    return adultsOver18.length > 0
  }
}

</script>

<style lang="scss" scoped>
.booking-summary{
  &__wrapper {
    background-color: #ffffff;
    padding: 0 20px 10px 20px;
    margin-top: 20px;

    @include mediaFluid(tablet) {
      padding-bottom: 30px;
    }
    @include mediaFluid(desktop) {
      max-width: 1260px;
      margin-left: auto;
      margin-right: auto;
      padding: 0 10px 30px;
      display: grid;
      grid-template-columns: 1fr 401px;
      grid-gap: 0 19px;
    }
  }
  &__heading {
    display: grid;
    padding: 27px 0 0 0;

    @include mediaFluid(tablet) {
        padding: 31px 0 0 0;
    }

    h1 {
      position: relative;
      color: #005CA9;
      font-family: 'Gotham';
      font-size: 26px;
      font-weight: bold;
      line-height: 26px;
      text-align: center;
      margin-bottom: 5px;
      margin-top: 0;

      @include mediaFluid(tablet) {
        text-align: left;
        font-size: 35px;
        margin-bottom: 8px;
      }
    }
    hr {
      margin: 0 auto;
      width: 36px;
      height: 7px;
      background-color: #005CA9;
      border: 0 none;

      @include mediaFluid(tablet) {
        display: none
      }

    }
    i {
      color: #005CA9;
      font-family: 'Georgia';
      font-size: 16px;
      position: relative;
      padding-left: 44px;

      &::before {
        content: '';
        position: absolute;
        left: 0;
        top: 5px;
        background-color: #005CA9;
        width: 36px;
        height: 7px;
      }
    }

  }
  &__hotel {
    margin: 14px 0 13px 0;
    display: flex;
    flex-wrap: wrap;
    align-items: center;

    @include mediaFluid(tablet) {
      margin: 9px 0 19px 0;
    }

    p {
      font-family: 'Gotham';
      font-size: 22px;
      color: #545454;
      font-weight: bold;
      line-height: normal;
      margin-right: 10px;

      @include mediaFluid(tablet) {
        font-size: 26px;
      }
    }

    .stars {
      display: inline-block;
    }
  }
  &__participants {

    h2 {
      margin: 0;
      font-family: 'Gotham';
      color: #005CA9;
      font-size: 16px;
      font-weight: bold;
      line-height: normal;
    }
    hr {
      margin: 3px auto 7px 0;
      width: 20px;
      height: 3px;
      background-color: #005CA9;
      border: 0 none;
    }
  }
  &__services {
    margin-top: 18px;

    @include mediaFluid(desktop) {
      margin-top: 0;
    }

    h2 {
      margin: 0;
      font-family: 'Gotham';
      color: #005CA9;
      font-size: 16px;
      font-weight: bold;
      line-height: normal;
      text-transform: uppercase;
    }
    hr {
      margin: 3px auto 7px 0;
      width: 20px;
      height: 3px;
      background-color: #005CA9;
      border: 0 none;
    }

    .icon__info {
      color: #005CA9;
      font-size: 13px;
      margin-left: 3px;
      align-self: flex-end;
    }
  }
  &__attractions {
    h2 {
      margin: 0;
      font-family: 'Gotham';
      color: #005CA9;
      font-size: 16px;
      font-weight: bold;
      line-height: normal;
      text-transform: uppercase;
    }
    hr {
      margin: 3px auto 7px 0;
      width: 20px;
      height: 3px;
      background-color: #005CA9;
      border: 0 none;
    }
  }
  &__pattern-element {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    border-bottom: 1px solid #adadad;
  }
  &__pattern-text {
    color: #545454;
    font-size: 12px;
    font-family: 'Gotham Book';
    font-weight: 300;
    position: relative;
    padding: 9px 15px 9px 0;

    &:hover .tooltip-wrapper {
      display: block;
    }
  }
  .horizontal-align {
    display: flex;
    flex-direction: row;
		align-items: center
  }
  .to-uppercase {
    text-transform: uppercase;
  }

  //-----------------------------------------------
  // Height extension for element on mobile version when text is too long
  .height-extension {
    min-height: 49px;
  }

  //-----------------------------------------------------------
  // Style for 2 types of price appearance elements
  .in-price {
    text-align: right;
    color: #005CA9;
    font-weight: 300;
    font-size: 12px;
    font-family: 'Gotham Book';
    line-height: 16px;
  }
  .price {
    display: flex;
    align-items: first baseline;
    text-align: right;
    color: #005CA9;
    font-weight: bold;
    font-size: 16px;

    span {
      font-family: "Gotham Book";
      font-weight: 300;
      font-size: 12px;
      margin-left: 5px;
    }
  }
  &__voucher {
    @include mediaFluid(tablet) {
        margin-top: 0;
    }

    h3{
      margin: 0 0 8px 0;
      color: #005CA9;
      font-family: "Gotham";
      font-size: 11px;
      font-weight: bold;
      line-height: normal;
    }

    input {
      margin: 0 0 16px 0;
      border: 0 none;
      background-color: #EDEDED;
      width: 100%;
      height: 36px;
      padding-left: 9px;
      color: #009FEA;
      font-size: 20px;
      font-weight: 300;
      font-family: 'Gotham Book';
    }
    .right-padding {

      padding-right: 84px;
    }

    input::placeholder {
      color: #005CA9;
      font-size: 18px;
      font-weight: 300;
      font-family: 'Gotham Book';
      opacity: 0.5;
    }
    input:focus {
      outline-style:none;
    }

    &--input-field {
      position: relative;
    }

    &--convert-button {
      position: absolute;
      display: flex;
      border: 0 none;
      background: none;
      padding: 0;
      top: 11px;
      right: 10px;
      color: #005CA9;
      font-size: 12px;
      font-weight: bold;
      font-family: 'Gotham';
      align-items: center;
    }

    &--convert-button:hover {
      color: #E71F6C;
    }

    .icon_arrow {
      font-size: 6px;
      transform: rotate(270deg);
      padding: 9px 0 0 3px;
    }

  }
  &__price {

    &--header {
      display: flex;
      flex-direction: row;
      align-items: center;

      h3 {
        margin: 0;
        font-size: 14px;
        font-family: 'Gotham';
        font-weight: bold;
        line-height: normal;
        color: #E71F6C;
      }

      .icon__info--red {
        color: #E71F6C;
        font-size: 14px;
        margin-left: 3px;
        align-self: flex-start;
      }
    }

    &--total {
      color: #E71F6C;
      font-size: 45px;
      font-weight: bold;
      font-family: 'Gotham';

      span {
        font-weight: 300;
        font-size: 22px;
        line-height: 12px;
        font-family: "Gotham Book";
      }

    }

    &--advance {
      color: #535353;
      font-family: 'Gotham';
      font-size: 20px;
      font-weight: bold;
      line-height: normal;

      span {
        font-weight: 300;
        font-family: 'Gotham Book';
        font-size: 13px;
      }
    }

    &--remaining {
      color: #535353;
      font-family: 'Gotham Book';
      font-size: 12px;
      font-weight: 300;
      line-height: 12px;
    }
  }

  //----------------------------------
  // style only for section appearance
  &__additional-service {
    @include mediaFluid(desktop) {
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-gap: 19px;
    }

    > div:first-child {
      margin-bottom: 20px;

      @include mediaFluid(desktop) {
        margin: 0;
      }
    }

    &--wrapper > div:first-child {
      margin-bottom: 20px;
    }
  }

  &__column {
    margin-top: 30px;

    @include mediaFluid(tablet) {
      display: grid;
      grid-template-columns: 41% 1fr;
      grid-gap: 0 16px;
    }

    @include mediaFluid(desktop) {
      grid-template-columns: 1fr;
      margin-top: 28px;
      align-items: end;
    }
  }

  .reservation-button {
    width: 100%;
    height: 70px;
    border: 0 none;
    background-color: #E71F6C;
    font-family: 'Gotham';
    font-weight: bold;
    color: #ffffff;
    font-size: 20px;
    text-align: center;
    cursor: pointer;

    @include mediaFluid(tablet) {
      margin: 11px 0 14px 0;
    }

    &:hover {
      opacity: 0.75;
    }
  }
}

//--------------------------
// style for Fluid Container
.fluid-container {
  max-width: 100%;
  background-color: #ffffff;

  @include mediaFluid(desktop) {
    padding: 0;
  }
}

//--------------------------
// style for Tooltip section
.booking-summary__services {
  .tooltip-wrapper {
    display: none;
    position: absolute;
    bottom: 30px;
  }

  &--toolTip {
    width: 360px;
    background-color: #ffffff;
    color: #005CA9;
    font-family: 'Gotham';
    font-size: 11px;
    line-height: 16px;
    display: flex;
    flex-direction: column;
    box-shadow: 0px 0px 3px 0px rgba(0, 0, 0, 0.4);
    padding: 10px 20px 20px;

    .icon__info-toolTip {
      font-size: 21px;
      text-align: center;
      margin: 0 0 9px 0;
    }

    h3 {
      font-size: 12px;
      font-weight: bold;
      margin: 0 0 9px 0;
    }
    p {
      color: #535353;
      font-weight: 300;
      font-family: 'Gotham Book';
      text-align: left;
    }

    .details {
      margin: 14px 0 0 0;
      font-weight: bold;
      text-decoration: underline;
      text-decoration-color: #005CA9;
      text-transform: uppercase;
      cursor: pointer;
    }

    &::after {
      content: '';
      position: absolute;
      width: 16px;
      height: 16px;
      top: 100%;
      left: 50%;
      transform: rotate(45deg);
      margin-left: -8px;
      margin-top: -9px;
      background-color: #ffffff;
      box-shadow: 1px 1px 2px -1px rgba(0, 0, 0, 0.4);
    }
  }
}

//------------------------
// style for Modal section
.booking-summary__modal {
  background-color: #ffffff;
  color: #005CA9;
  padding: 60px 64px;

  &--header {
    display: flex;
    flex-direction: row;
    margin-bottom: 5px;
    justify-content: center;
    align-items: center;

    .icon__info {
      font-size: 28px;
      margin-right: 8px;
      padding-bottom: 5px;
    }
  }
  hr {
    margin: 0 auto 28px;
    width: 54px;
    height: 6px;
    background-color: #005CA9;
    border: 0 none;
  }

  &--price-content {
    display: flex;
    flex-direction: row;
    border-top: 1px solid #adadad;
    height: 35px;
    align-items: center;

    .icon__form-valid-check {
      font-size: 9px;
      color: #00C25B;
      padding-left: 5px;
      margin-right: 8px;
    }

    p {
      text-transform: uppercase;
      font-size: 12px;
      font-weight: 300;
      font-family: 'Gotham Book';
    }
    &:last-child {
      border-bottom: 1px solid #adadad;
    }
  }

  h1 {
    margin: 0;
    color: #005CA9;
    font-size: 35px;
    font-weight: bold;
    line-height: 38px;
    font-family: "Gotham";
  }
}

.reservation-process {
  &__headline {
    font-family: 'Gotham';
    font-weight: bold;
    font-size: 25px;
    color: #005CA9;
    text-transform: uppercase;
    text-align: center;

    @include media(tablet) {
      font-size: 36px;
    }
  }

  &__loader {
    width: auto;
    height: auto;
    position: relative;
    left: auto;
    transform: translate(0, -20%);
  }

  &__description {
    font-family: 'Gotham Book';
    color: #535353;
  }

  hr {
    margin: 8px auto 37px;
    width: 54px;
    height: 6px;
    background-color: #005CA9;
    border: none;
  }
}
</style>

<!-- For css that can't be written in 'scoped' mode -->
<style lang="scss">
.booking-summary__contract-template {
  margin-top: 17px;
  color: #787878;
  font-family: 'Gotham Book';
  font-size: 12px;
  font-weight: 300;
  line-height: 14px;

  a {
    color:#787878;
    text-decoration: underline;
    text-decoration-color: #adadad;
  }
}
.booking-summary .details-modal .modal-dialog__wrapper {
  max-width: 1050px;
}
</style>



// WEBPACK FOOTER //
// src/components/booking/Summary.vue